<script lang="ts">
import { defineComponent, nextTick, onUnmounted, ref, watch, type PropType } from 'vue'
import Compressor from 'compressorjs'

export default defineComponent({
  name: 'ImageThumbnail',
  components: {},
  props: {
    imageList: {
      type: Array as PropType<File[]>,
      required: true
    }
  },
  emits: ['deleteImage'],
  setup(props, { emit }) {
    /** 圧縮済みの画像URLリスト */
    const compressedImageUrlList = ref<string[]>([])
    /** 画像カルーセルモーダルの表示フラグ */
    const isCarouselVisible = ref<boolean>(false)
    /** 選択中の画像インデックス */
    const selectedImageIndex = ref<number>(-1)

    /**
     * 画像サイズを軽量化する
     * @param file 対象の画像ファイル
     * @param quality 画質 0 - 1 の少数
     * @param maxWidth 最大横幅px
     * @param maxHeight 最大縦幅px
     * @returns 圧縮済みファイル
     */
    const compress = async (
      file: File,
      quality = 0.9,
      maxWidth = 80,
      maxHeight = 80
    ): Promise<Blob> => {
      return await new Promise<Blob>((resolve, reject) => {
        new Compressor(file, { quality, maxWidth, maxHeight, success: resolve, error: reject })
      })
    }

    /**
     * 圧縮した画像リストを作成する
     * @param imageList 圧縮前の画像リスト
     * @returns 圧縮済みの画像URLリスト
     */
    const createCompressedImageUrlList = async (imageList: File[]): Promise<string[]> => {
      /** 表示する画像サムネイルの上限サイズ（15KB） */
      const IMAGE_DISPLAY_MAX_SIZE: number = 1024 * 15
      return Promise.all(
        imageList.map(async e => {
          const compressedImage = await compress(e)
          if (compressedImage.size > IMAGE_DISPLAY_MAX_SIZE) {
            return ''
          }
          return URL.createObjectURL(compressedImage)
        })
      )
    }

    /**
     * 表示対象の画像リストをリセットする
     * @param なし
     * @returns なし
     * @note 必ず既存のURLを解放する
     */
    const resetCompressedImageUrlList = async () => {
      // 既存のURLを解放
      compressedImageUrlList.value.forEach(url => {
        if (url !== undefined) {
          URL.revokeObjectURL(url)
        }
      })
      // 新しいURLを設定
      compressedImageUrlList.value = await createCompressedImageUrlList(props.imageList)
    }

    /**
     * 画像カルーセルを表示する
     * @param 選択中の画像インデックス
     * @returns なし
     */
    const openCarousel = async (index: number) => {
      selectedImageIndex.value = index
      await nextTick()
      isCarouselVisible.value = true
    }

    /**
     * 画像カルーセルを閉じる
     * @param なし
     * @returns なし
     */
    const closeCarousel = (): void => {
      isCarouselVisible.value = false
    }

    /**
     * 画像削除処理
     * @param index 画像インデックス
     * @returns なし
     */
    const deleteImage = async (index: number): Promise<void> => {
      // 既存のURLを解放
      URL.revokeObjectURL(compressedImageUrlList.value[index])
      // 削除した画像のインデックスをemit
      emit('deleteImage', index)
    }

    /**
     * 画像リストの更新を検知し、表示対象をリセットする
     */
    watch(
      () => props.imageList,
      async () => {
        await resetCompressedImageUrlList()
      },
      { immediate: true } // `immediate: true` で初回実行も含める
    )

    onUnmounted(() => {
      // URLを解放する
      compressedImageUrlList.value.forEach(url => {
        if (url !== undefined) {
          URL.revokeObjectURL(url)
        }
      })
    })

    // 定義された関数やデータを返却
    return {
      isCarouselVisible,
      selectedImageIndex,
      compressedImageUrlList,
      openCarousel,
      closeCarousel,
      deleteImage
    }
  }
})
</script>

<template>
  <div class="thumbnail-container">
    <!-- 画像リストをループしてサムネイルを作成 -->
    <div v-for="(image, index) in compressedImageUrlList" :key="index" class="thumbnail">
      <img :src="image" :alt="'thumbnail-' + index" @click="openCarousel(index)" />
    </div>
    <ImageCarousel
      v-if="isCarouselVisible"
      :slides="compressedImageUrlList"
      :selectedImageIndex="selectedImageIndex"
      @closeCarousel="closeCarousel"
      @deleteImage="deleteImage"
    />
  </div>
</template>

<style scoped>
.thumbnail-container {
  display: flex;
  flex-wrap: wrap; /* 自動改行 */
  gap: 10px; /* サムネイル間のスペース */
}

.thumbnail {
  width: 80px; /* サムネイルの幅を指定 */
  height: 80px; /* サムネイルの高さを指定 */
  overflow: hidden; /* 画像が領域をはみ出さないようにする */
  border: 1px solid #ccc; /* 枠線を付ける */
  border-radius: 5px; /* 角を丸くする場合 */
  display: flex;
  justify-content: center;
  align-items: center;
}

.thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 比率を維持しつつサムネイル領域に収める */
}
</style>
